import React, { Component } from "react";
import { Stage, Layer, Line, Text } from "react-konva";
import { SERVER_HTTP, SERVER_WS } from "./serverConfig";

/**
 * The minimum squared distance required between two points to add a new point
 * when drawing with the pencil tool.
 * @constant {number}
 */
const PENCIL_DISTANCE_SQ = 50;

/**
 * The minimum squared distance required between two points to add a new point
 * when drawing with the eraser tool.
 * @constant {number}
 */
const ERASER_DISTANCE_SQ = 25;

/**
 * A static class implementing the Ramer-Douglas-Peucker algorithm for line simplification.
 */
class RamerDouglasPeucker {
  /**
   * Calculates the perpendicular distance between a point and a line segment.
   * @param {Object} point - The point to calculate the distance from. Must have 'x' and 'y' properties.
   * @param {Object} lineStart - The starting point of the line segment. Must have 'x' and 'y' properties.
   * @param {Object} lineEnd - The ending point of the line segment. Must have 'x' and 'y' properties.
   * @returns {number} The perpendicular distance between the point and the line segment.
   */
  static getPerpendicularDistance(point, lineStart, lineEnd) {
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;

    if (dx === 0 && dy === 0) {
      return Math.sqrt(
        (point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2,
      );
    }

    const numerator = Math.abs(
      dy * point.x -
        dx * point.y +
        lineEnd.x * lineStart.y -
        lineEnd.y * lineStart.x,
    );
    const denominator = Math.sqrt(dx * dx + dy * dy);
    return numerator / denominator;
  }

  /**
   * Recursively applies the Ramer-Douglas-Peucker algorithm to simplify a set of points.
   * @param {Array<Object>} points - An array of point objects, each with 'x' and 'y' properties.
   * @param {number} epsilon - The tolerance value for simplification.  Higher values result in more aggressive simplification.
   * @returns {Array<Object>} A new array of simplified point objects.
   */
  static rdp(points, epsilon) {
    if (points.length < 3) return points;

    let dmax = 0;
    let index = 0;

    for (let i = 1; i < points.length - 1; i++) {
      const d = this.getPerpendicularDistance(
        points[i],
        points[0],
        points[points.length - 1],
      );
      if (d > dmax) {
        index = i;
        dmax = d;
      }
    }

    if (dmax > epsilon) {
      const recResults1 = this.rdp(points.slice(0, index + 1), epsilon);
      const recResults2 = this.rdp(points.slice(index), epsilon);
      return recResults1.slice(0, -1).concat(recResults2);
    } else {
      return [points[0], points[points.length - 1]];
    }
  }

  /**
   * Simplifies a line represented as a flat array of coordinates using the Ramer-Douglas-Peucker algorithm.
   * @param {Array<number>} flatPoints - An array of x and y coordinates in the format [x1, y1, x2, y2, ...].
   * @param {number} epsilon - The tolerance value for simplification.
   * @returns {Array<number>} A new array of simplified x and y coordinates.
   */
  static simplifyLine(flatPoints, epsilon) {
    const pts = [];
    for (let i = 0; i < flatPoints.length; i += 2) {
      pts.push({ x: flatPoints[i], y: flatPoints[i + 1] });
    }

    const simplified = this.rdp(pts, epsilon);
    const flatSimplified = [];
    simplified.forEach((pt) => {
      flatSimplified.push(pt.x, pt.y);
    });
    return flatSimplified;
  }
}

/**
 * DrawingBoard component responsible for rendering a drawing canvas.
 */
class DrawingBoard extends Component {
  /**
   * Creates an instance of DrawingBoard.
   * @param {Object} props - Component properties.  Must include a 'tool' property indicating the current drawing tool.
   *                         May include an 'rdpTolerance' property to specify the RDP simplification tolerance.
   */
  constructor(props) {
    super(props);

    /**
     * @typedef {Object} Line
     * @property {string} tool - The tool used to draw the line ('pencil' or 'eraser').
     * @property {number[]} points - An array of x and y coordinates representing the line's points.
     */

    /**
     * @typedef {Object} DrawingBoardState
     * @property {Line[]} lines - An array of Line objects representing the drawn lines.
     * @property {boolean} consoleVisible - A boolean indicating whether the console panel is visible.
     * @property {number} stageScale - The current scale factor of the stage.
     * @property {number} stageWidth - The width of the stage.
     * @property {number} stageHeight - The height of the stage.
     */

    /**
     * @type {DrawingBoardState}
     */
    this.state = {
      lines: [],
      consoleVisible: false,
      isConnected: false,
      stageScale: 1,
      stageWidth: 1920,
      stageHeight: 1080,
      isMobile: false,
      showPasswordModal: false,
      passwordInput: "",
      passwordError: "",
      currentTool: "pencil", // Default tool set to pencil
      selectedColor: "#000000",
    };

    // Adjust distance thresholds for mobile
    this.MOBILE_PENCIL_DISTANCE_SQ = 25; // More sensitive for mobile
    this.MOBILE_ERASER_DISTANCE_SQ = 15;

    /**
     * A boolean flag indicating whether the user is currently drawing.
     * @type {boolean}
     */
    this.isDrawing = false;

    this.ws = null;
    this.stageRef = React.createRef();
    this.containerRef = React.createRef();

    // WebSocket reconnection logic
    this.wsRetryCount = 0;
    this.maxWsRetries = 10; // Maximum number of reconnection attempts
    this.baseRetryDelay = 3000; // Initial delay in ms

    // Bind methods to ensure correct `this` context.
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.toggleConsole = this.toggleConsole.bind(this);
    this.initWebSocket = this.initWebSocket.bind(this);
    this.handleWebSocketMessage = this.handleWebSocketMessage.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);
    this.checkIfMobile = this.checkIfMobile.bind(this);
    this.preventTouchDefault = this.preventTouchDefault.bind(this);
    this.openPasswordModal = this.openPasswordModal.bind(this);
    this.closePasswordModal = this.closePasswordModal.bind(this);
    this.handlePasswordChange = this.handlePasswordChange.bind(this);
    this.handlePasswordSubmit = this.handlePasswordSubmit.bind(this);
    this.toggleTool = this.toggleTool.bind(this);
  }

  componentDidMount() {
    this.initWebSocket();
    this.loadInitialDrawing();
    this.updateDimensions();
    this.checkIfMobile();
    window.addEventListener("resize", this.updateDimensions);
    window.addEventListener("resize", this.checkIfMobile);

    // Prevent unwanted touch behaviors
    const stage = this.stageRef.current;
    if (stage) {
      stage.addEventListener("touchstart", this.preventTouchDefault, {
        passive: false,
      });
      stage.addEventListener("touchmove", this.preventTouchDefault, {
        passive: false,
      });
      stage.addEventListener("touchend", this.preventTouchDefault, {
        passive: false,
      });
    }
  }

  componentWillUnmount() {
    if (this.ws) {
      this.ws.close();
    }
    // Clear reconnect timer if present
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    window.removeEventListener("resize", this.updateDimensions);
    window.removeEventListener("resize", this.checkIfMobile);

    const stage = this.stageRef.current;
    if (stage) {
      stage.removeEventListener("touchstart", this.preventTouchDefault);
      stage.removeEventListener("touchmove", this.preventTouchDefault);
      stage.removeEventListener("touchend", this.preventTouchDefault);
    }
  }

  initWebSocket() {
    this.ws = new WebSocket(SERVER_WS);

    // Track reconnect timer to avoid multiple timers
    if (!this.reconnectTimer) this.reconnectTimer = null;

    this.ws.onopen = () => {
      this.setState({ isConnected: true });
      this.wsRetryCount = 0; // Reset retry counter on successful connection
      // Clear any pending reconnect timer
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
    };

    this.ws.onclose = () => {
      this.setState({ isConnected: false });
      // Attempt to reconnect with exponential backoff, up to maxWsRetries
      if (this.wsRetryCount < this.maxWsRetries) {
        const delay = this.baseRetryDelay * Math.pow(2, this.wsRetryCount); // Exponential backoff
        if (!this.reconnectTimer) {
          this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.wsRetryCount += 1;
            this.initWebSocket();
          }, delay);
        }
      } else {
        // Optionally, notify user that max retries reached
        console.error("WebSocket: Maximum reconnection attempts reached.");
      }
    };

    this.ws.onmessage = this.handleWebSocketMessage;
  }

  handleWebSocketMessage(event) {
    const data = JSON.parse(event.data);

    switch (data.type) {
      case "init":
        // Ensure data.lines is an array before setting state
        const linesDataInit = Array.isArray(data.lines) ? data.lines : [];
        this.setState({ lines: linesDataInit });
        break;
      case "draw":
        // Ensure data.line exists before adding
        if (data.line && Array.isArray(this.state.lines)) {
          this.setState((prevState) => ({
            // Ensure prevState.lines is an array before spreading
            lines: [
              ...(Array.isArray(prevState.lines) ? prevState.lines : []),
              data.line,
            ],
          }));
        } else {
          console.warn(
            "Received draw message with no line data or lines state is not an array:",
            data,
            this.state.lines,
          );
        }
        break;
      case "clear":
        this.setState({ lines: [] });
        break;
      // It might be good practice to add a default case
      default:
        console.warn("Received unknown WebSocket message type:", data.type);
    }
  }

  async loadInitialDrawing() {
    try {
      const response = await fetch(`${SERVER_HTTP}/drawing`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // Ensure data.lines is an array before setting state
      const linesData = Array.isArray(data.lines) ? data.lines : [];
      this.setState({ lines: linesData });
    } catch (error) {
      console.error("Failed to load initial drawing:", error);
      // Optionally set lines to empty array on error too
      this.setState({ lines: [] });
    }
  }

  // Add new clear handler method
  async handleClear() {
    try {
      await fetch(`${SERVER_HTTP}/clear`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: "password" }),
      });
    } catch (error) {
      console.error("Failed to clear drawing:", error);
    }
  }

  updateDimensions() {
    if (!this.containerRef.current) return;

    const containerWidth = this.containerRef.current.offsetWidth;
    const scale = containerWidth / this.state.stageWidth;

    this.setState({
      stageScale: scale,
    });
  }

  checkIfMobile() {
    const isMobile = window.innerWidth <= 768;
    this.setState({ isMobile });
  }

  preventTouchDefault(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  /**
   * Handler for mouse/touch down events.
   * Initiates a new line drawing.
   * @param {Object} e - The event object from Konva.
   */
  handleMouseDown(e) {
    e.evt.preventDefault();
    this.isDrawing = true;
    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    const scale = this.state.stageScale;

    const pos = {
      x: point.x / scale,
      y: point.y / scale,
    };

    const newLine = {
      tool: this.state.currentTool,
      color:
        this.state.currentTool === "pencil"
          ? this.state.selectedColor
          : "#ffffff",
      points: [pos.x, pos.y],
    };
    this.setState((prevState) => ({
      lines: [...prevState.lines, newLine],
    }));
  }

  /**
   * Handler for mouse/touch move events.
   * Adds a point to the current line if the pointer has moved enough distance.
   * @param {Object} e - The event object from Konva.
   */
  handleMouseMove(e) {
    e.evt.preventDefault();
    if (!this.isDrawing) return;

    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    const scale = this.state.stageScale;

    const pos = {
      x: point.x / scale,
      y: point.y / scale,
    };

    const { lines, isMobile } = this.state;

    if (lines.length === 0) return;

    const currentLine = lines[lines.length - 1];

    if (currentLine.points.length >= 2) {
      const [lastX, lastY] = currentLine.points.slice(-2);
      const dx = pos.x - lastX;
      const dy = pos.y - lastY;
      const distanceSq = dx * dx + dy * dy;

      // Use mobile-optimized distance thresholds on mobile devices
      const minDistanceSq =
        currentLine.tool === "eraser"
          ? isMobile
            ? this.MOBILE_ERASER_DISTANCE_SQ
            : ERASER_DISTANCE_SQ
          : isMobile
            ? this.MOBILE_PENCIL_DISTANCE_SQ
            : PENCIL_DISTANCE_SQ;

      if (distanceSq < minDistanceSq) return;
    }

    const updatedLine = {
      ...currentLine,
      points: currentLine.points.concat([pos.x, pos.y]),
    };

    this.setState((prevState) => ({
      lines: [...prevState.lines.slice(0, -1), updatedLine],
    }));
  }

  /**
   * Handler for mouse/touch up events.
   * Concludes the current drawing action, and applies the RDP simplification.
   */
  handleMouseUp() {
    if (!this.isDrawing) return;
    const { lines } = this.state;
    if (lines.length > 0) {
      const currentLine = lines[lines.length - 1];
      const tolerance =
        this.props.rdpTolerance !== undefined ? this.props.rdpTolerance : 2;
      const simplifiedPoints = RamerDouglasPeucker.simplifyLine(
        currentLine.points,
        tolerance,
      );
      const updatedLine = { ...currentLine, points: simplifiedPoints };

      // Send the line to the server
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(
          JSON.stringify({
            type: "draw",
            line: updatedLine,
          }),
        );
      }

      this.setState((prevState) => ({
        lines: [...prevState.lines.slice(0, -1), updatedLine],
      }));
    }
    this.isDrawing = false;
  }

  /**
   * Toggles the visibility of the console panel.
   */
  toggleConsole() {
    this.setState((prevState) => ({
      consoleVisible: !prevState.consoleVisible,
    }));
  }

  openPasswordModal() {
    this.setState({
      showPasswordModal: true,
      passwordInput: "",
      passwordError: "",
    });
  }

  closePasswordModal() {
    this.setState({
      showPasswordModal: false,
      passwordInput: "",
      passwordError: "",
    });
  }

  handlePasswordChange(e) {
    this.setState({ passwordInput: e.target.value });
  }

  async handlePasswordSubmit(e) {
    e.preventDefault();
    const { passwordInput } = this.state;
    try {
      const response = await fetch(`${SERVER_HTTP}/clear`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password: passwordInput }),
      });
      if (!response.ok) {
        throw new Error("Invalid password");
      }
      this.closePasswordModal();
    } catch (error) {
      this.setState({ passwordError: "Incorrect password. Please try again." });
    }
  }

  /**
   * Toggles the current drawing tool between 'pencil' and 'eraser'.
   */
  toggleTool() {
    this.setState((prevState) => ({
      currentTool: prevState.currentTool === "pencil" ? "eraser" : "pencil",
    }));
  }

  /**
   * Renders the DrawingBoard component.
   * @returns {JSX.Element} The rendered component.
   */
  render() {
    const {
      lines,
      consoleVisible,
      isConnected,
      stageScale,
      stageWidth,
      stageHeight,
      isMobile,
      showPasswordModal,
      currentTool,
      // selectedColor,
      // passwordInput,
    } = this.state;

    return (
      <div
        id="draw"
        className="page-section draw-section"
        style={{ marginBottom: "100px" }}
      >
        <div className="drawingboard" aria-label="Drawing Board">
          <div
            ref={this.containerRef}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "1920px",
              aspectRatio: "16/9",
              touchAction: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div
              style={{
                zIndex: 1,
                display: "flex",
                gap: isMobile ? 5 : 8,
                marginBottom: "10px",
              }}
            >
              <button
                style={{
                  padding: isMobile ? "6px 10px" : "8px 12px",
                  cursor: "pointer",
                  fontSize: isMobile ? "14px" : "16px",
                  backgroundColor: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                }}
                onClick={this.toggleTool}
              >
                Switch to {currentTool === "pencil" ? "Eraser" : "Pencil"}
              </button>
              <input
                type="color"
                value={this.state.selectedColor}
                onChange={(e) =>
                  this.setState({ selectedColor: e.target.value })
                }
                style={{
                  width: isMobile ? 30 : 40,
                  height: isMobile ? 30 : 40,
                  padding: 2,
                  borderRadius: "4px",
                  cursor: "pointer",
                  border: "2px solid #ffffff",
                }}
                title="Select drawing color"
              />
            </div>
            <button
              style={{
                position: "absolute",
                top: isMobile ? 5 : 10,
                right: isMobile ? 5 : 10,
                zIndex: 1,
                padding: isMobile ? "6px 10px" : "8px 12px",
                cursor: "pointer",
                fontSize: isMobile ? "14px" : "16px",
              }}
              onClick={this.toggleConsole}
            >
              {consoleVisible ? "Hide" : "Show"} Console
            </button>
            <Stage
              ref={this.stageRef}
              width={stageWidth}
              height={stageHeight}
              onMouseDown={this.handleMouseDown}
              onMouseMove={this.handleMouseMove}
              onMouseUp={this.handleMouseUp}
              onTouchStart={this.handleMouseDown}
              onTouchMove={this.handleMouseMove}
              onTouchEnd={this.handleMouseUp}
              style={{
                backgroundColor: "#f0f0f0",
                boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                borderRadius: "4px",
                width: "100%",
                height: "100%",
                touchAction: "none",
              }}
              scaleX={stageScale}
              scaleY={stageScale}
            >
              <Layer>
                <Text
                  text={`Current Tool: ${currentTool === "pencil" ? "Pencil" : "Eraser"}`}
                  x={10}
                  y={isMobile ? 10 : 20}
                  fontSize={isMobile ? 14 : 16}
                />
                {lines.map((line, i) => (
                  <Line
                    key={i}
                    points={line.points}
                    stroke={line.color}
                    strokeWidth={
                      line.tool === "eraser"
                        ? isMobile
                          ? 30
                          : 20
                        : isMobile
                          ? 3
                          : 5
                    }
                    tension={0.5}
                    lineCap="round"
                    lineJoin="round"
                    globalCompositeOperation={
                      line.tool === "eraser" ? "destination-out" : "source-over"
                    }
                  />
                ))}
              </Layer>
            </Stage>
          </div>
          {consoleVisible && (
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: "300px",
                height: "calc(100vh - 20px)",
                margin: "10px",
                backgroundColor: "rgba(40, 44, 52, 0.9)",
                border: "1px solid #555",
                borderRadius: "5px",
                overflowY: "auto",
                padding: "10px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
                fontSize: "12px",
                fontFamily: "monospace",
                color: "#cccccc",
              }}
            >
              <h2 style={{ margin: 0, color: "#cccccc" }}>Lines Console</h2>
              <p>
                Current Tool: <strong>{currentTool}</strong>
              </p>
              <p>Total Lines: {lines.length}</p>
              <hr style={{ margin: "10px 0", borderColor: "#555" }} />
              {lines.map((line, index) => (
                <div
                  key={index}
                  style={{ marginBottom: "5px", wordBreak: "break-all" }}
                >
                  Line {index + 1} ({line.tool}): {line.points.length / 2}{" "}
                  points
                </div>
              ))}
              <div
                style={{
                  marginTop: "20px",
                  paddingTop: "10px",
                  borderTop: "1px solid #555",
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    padding: "4px 8px",
                    backgroundColor: isConnected ? "#4CAF50" : "#f44336",
                    color: "white",
                    borderRadius: "4px",
                    fontSize: "12px",
                    textAlign: "center",
                  }}
                >
                  {isConnected ? "Connected" : "Disconnected"}
                </div>
                <button
                  onClick={this.openPasswordModal}
                  style={{
                    padding: "4px 8px",
                    cursor: "pointer",
                    backgroundColor: "#ff4444",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
          )}
          {showPasswordModal && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  background: "#3a3a3a",
                  padding: "24px",
                  borderRadius: "8px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                  minWidth: "300px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <h3 style={{ color: "#cccccc" }}>Enter Password to Clear</h3>
                <form
                  onSubmit={this.handlePasswordSubmit}
                  style={{ width: "100%" }}
                >
                  <input
                    type="password"
                    value={this.state.passwordInput}
                    onChange={this.handlePasswordChange}
                    placeholder="Password"
                    style={{
                      width: "100%",
                      padding: "8px",
                      marginBottom: "10px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      fontSize: "16px",
                    }}
                    autoFocus
                  />
                  {this.state.passwordError && (
                    <div style={{ color: "red", marginBottom: "10px" }}>
                      {this.state.passwordError}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <button
                      type="button"
                      onClick={this.closePasswordModal}
                      style={{
                        padding: "6px 12px",
                        background: "#555",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        marginRight: "10px",
                        color: "white",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      style={{
                        padding: "6px 12px",
                        background: "#ff4444",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default DrawingBoard;
